import { STEP_ACTIONS, STEP_STATUSES_WORTH_SHOWING } from '../model/constants';
import showServerError from '@shared/lib/showError';
// .NET TimeSpan "d.hh:mm:ss". The builder only exposes days + hours.
export const buildTimeSpan = (days, hours) => {
  const d = Number(days) || 0;
  const h = Number(hours) || 0;
  if (d === 0 && h === 0) return null;
  return `${d}.${String(h).padStart(2, '0')}:00:00`;
};

// "Send after N days" (API 2026-10-15): the step's action runs only
// after this delay, counted from entering the step. The builder exposes
// days + hours; the API accepts at least one hour and at most 365 days.
export const START_DELAY_MIN_HOURS = 1;
export const START_DELAY_MAX_DAYS = 365;

export const START_DELAY_ERROR = { tooShort: 'tooShort', tooLong: 'tooLong' };

/**
 * A card's `delay` is `null` for "send right away" and `{ days, hours }`
 * once the author chose "after a delay" - the choice is the shape, not
 * the numbers, so an emptied field never flips the card back to "right
 * away". `null` when the delay is valid or absent, else which rule it
 * breaks - mirrors the API's own check so the 400 on `steps.startDelay`
 * is never the first thing the author hears; a chosen delay with no
 * time in it breaks the one-hour rule.
 */
export const validateStartDelay = delay => {
  if (delay === null || delay === undefined) return null;
  const d = Number(delay.days) || 0;
  const h = Number(delay.hours) || 0;
  const totalHours = d * 24 + h;
  if (totalHours < START_DELAY_MIN_HOURS) return START_DELAY_ERROR.tooShort;
  if (totalHours > START_DELAY_MAX_DAYS * 24) return START_DELAY_ERROR.tooLong;
  return null;
};

// "If not completed within N days" on a waiting step: optional, and when
// set the API accepts at most 365 days (`steps.waitFor.timeoutDuration`).
export const WAIT_TIMEOUT_MAX_DAYS = 365;

export const WAIT_TIMEOUT_ERROR = { tooLong: 'tooLong' };

/**
 * `null` when the card is not waiting, has no timeout (no time in it)
 * or the timeout is within the ceiling, else which rule it breaks - the
 * field snaps typed digits to the ceiling, this catches what still gets
 * through (hours on top of 365 days, a pasted value) before the API's 422.
 */
export const validateWaitTimeout = wait => {
  if (!wait?.enabled) return null;
  const d = Number(wait.timeoutDays) || 0;
  const h = Number(wait.timeoutHours) || 0;
  if (d * 24 + h > WAIT_TIMEOUT_MAX_DAYS * 24) return WAIT_TIMEOUT_ERROR.tooLong;
  return null;
};

export const parseTimeSpan = spanString => {
  if (!spanString) return { days: 0, hours: 0 };
  // .NET omits the day segment entirely when it is zero (e.g. "05:00:00"
  // instead of "0.05:00:00"), so the day group must be optional here.
  const match = /^(?:(\d+)\.)?(\d{2}):/.exec(spanString);
  if (!match) return { days: 0, hours: 0 };
  return { days: Number(match[1] || 0), hours: Number(match[2]) };
};

const DEFAULT_PAGE_SIZE = 50;

// Journey list query params are all-lowercase on the wire.
export const buildJourneysListParams = ({
  lifecycleState,
  patientUuid,
  definitionUuid,
  continuationToken,
  pageSize,
} = {}) => {
  const params = { pageSize: pageSize || DEFAULT_PAGE_SIZE };
  if (lifecycleState) params.lifecyclestate = lifecycleState;
  if (patientUuid) params.patientuuid = patientUuid;
  if (definitionUuid) params.programdefinitionuuid = definitionUuid;
  if (continuationToken) params['continuation-token'] = continuationToken;
  return params;
};

// The completion event the engine waits on, by the resource's catalog
// type: articles and audio both report `resourceEngaged`, a video
// reports `videoCompleted`.
const EVENT_TYPE_BY_ACTION = {
  [STEP_ACTIONS.sendSurvey]: 'surveyCompleted',
  [STEP_ACTIONS.sendArticle]: 'resourceEngaged',
  [STEP_ACTIONS.sendAudio]: 'resourceEngaged',
  [STEP_ACTIONS.sendVideo]: 'videoCompleted',
};

// Editor cards -> API steps. Linear chain only: generated step keys,
// auto `next` links, no entry/branch conditions. For survey steps the
// matchRule is auto-scoped to the step's own asset (spec caveat: a null
// matchRule advances on ANY event of that type for the patient). The
// payload key for resource/video events is not documented, so those
// keep matchRule null.
export const chainToSteps = cards =>
  cards.map((card, index) => {
    const key = `step${index + 1}`;
    const isLast = index === cards.length - 1;
    let waitFor = null;
    if (card.wait?.enabled) {
      const timeoutDuration = buildTimeSpan(card.wait.timeoutDays, card.wait.timeoutHours);
      waitFor = {
        eventType: EVENT_TYPE_BY_ACTION[card.action],
        matchRule:
          card.action === STEP_ACTIONS.sendSurvey
            ? { ruleType: 'equals', surveyItemKey: 'surveyType', valueKey: card.assetRef }
            : null,
        timeoutDuration,
        onTimeout: timeoutDuration ? card.wait.onTimeout : null,
      };
    }
    return {
      key,
      name: card.name,
      action: card.action,
      assetRef: card.assetRef,
      entryCondition: null,
      // null = send the moment the step is entered (the default).
      startDelay: buildTimeSpan(card.delay?.days, card.delay?.hours),
      waitFor,
      next: isLast ? [] : [{ nextKey: `step${index + 2}`, branchCondition: null }],
    };
  });

export const stepsToChain = steps =>
  steps.map(step => {
    const { days, hours } = parseTimeSpan(step.waitFor?.timeoutDuration);
    return {
      name: step.name,
      action: step.action,
      assetRef: step.assetRef,
      // null = right away; see validateStartDelay for the shape.
      delay: step.startDelay ? parseTimeSpan(step.startDelay) : null,
      wait: step.waitFor
        ? {
            enabled: true,
            timeoutDays: days,
            timeoutHours: hours,
            onTimeout: step.waitFor.onTimeout || 'skip',
          }
        : { enabled: false },
    };
  });

// The linear builder can only re-save graphs it can fully represent.
export const isLinearDefinition = steps =>
  (steps || []).every((step, index) => {
    if (step.entryCondition) return false;
    const isLast = index === steps.length - 1;
    if (isLast) return (step.next || []).length === 0;
    return (
      (step.next || []).length === 1 &&
      step.next[0].nextKey === (steps[index + 1] || {}).key &&
      !step.next[0].branchCondition
    );
  });

// Map HTTP status + context to a `journeys.*` translation key. The
// backend's debug strings are never shown.
//
// 401 and 403 map to NOTHING on purpose: the fetch response interceptor
// already raises "You do not have permission to this resource" for both,
// so a feature adding its own message here would stack two snackbars on
// one failed request. Callers must treat a null key as "already
// reported" — use showJourneyError, which does.
export const getJourneyErrorKey = (status, context, errorCode) => {
  if (status === 401 || status === 403) return null;
  if (context === 'start') {
    if (status === 400) return 'alreadyEnrolled';
    if (status === 404) return 'programNotAvailable';
  }
  if (context === 'definitionSave') {
    if (status === 409) return 'programLocked';
    // 400: the steps; 422: a field the API validates (the description
    // over 2000 characters) — the editor checks that first, this is
    // the fallback.
    if (status === 400 || status === 422) return 'programInvalid';
    if (status === 404) return 'programNotFound';
  }
  // Deleting a definition: the API answers 409 with a distinct
  // `errorCode` per reason, so the user is told which rule stopped them
  // instead of "something went wrong". An unrecognised code falls
  // through to the generic message rather than being guessed at.
  if (context === 'definitionDelete') {
    if (status === 404) return 'programNotFound';
    if (status === 409) {
      if (errorCode === 'NotOwned') return 'deleteNotOwned';
      if (errorCode === 'NotDraft') return 'deleteNotDraft';
      if (errorCode === 'HasJourneys') return 'deleteHasJourneys';
    }
  }
  if (context === 'override') {
    if (status === 400) return 'actionNotAllowed';
    if (status === 404) return 'journeyNotFound';
    if (status === 409) return 'concurrentChange';
  }
  return 'genericError';
};

/**
 * Report a failed journey request as exactly one snackbar.
 *
 * Wraps the key lookup so the "the interceptor already spoke" rule lives
 * in one place rather than in every call site.
 */
export const showJourneyError = (dispatch, journeys, status, context, errorCode) => {
  const key = getJourneyErrorKey(status, context, errorCode);
  if (!key) return;
  showServerError(dispatch, journeys[key]);
};

/**
 * Journey-state steps (`GET program-journeys/{uuid}` -> `steps[]`,
 * backend 2026-09-09) keyed by stepKey. Only steps the journey has
 * reached are listed, so a definition step with no entry here is one
 * the journey has not arrived at yet.
 */
export const indexStepStates = steps =>
  (Array.isArray(steps) ? steps : []).reduce((acc, step) => {
    if (step?.stepKey) acc[step.stepKey] = step;
    return acc;
  }, {});

/**
 * What a step row shows for its status (brief 2026-09-18, design
 * 2026-09-19). A step has two statuses: the engine's (`status`:
 * pending / waiting / completed / skipped / failed) and, for a survey,
 * the assessment's own (`assetStatus`: invited / staled / completed
 * ...). A step whose template has no `waitFor` is "completed" the
 * moment the assessment is sent, so the engine's word next to an
 * assessment the patient has not opened read as a bug.
 *
 * The row therefore carries ONE badge, always about the patient's side:
 *   asset — the item's own status, whatever kind of item it is;
 *   sent  — the fallback for an item that reports no status, which is
 *           all the row can say: the program sent it.
 * The engine's word is a plain note under the step name, and only when
 * it adds something (waiting, skipped, failed, timed out) — the row's
 * done / current / upcoming look already says the rest.
 *
 * The asset branch was gated on `assetType === 'survey'` because only a
 * survey ever carried a status: the journey-state read resolves
 * `assetStatus` from the Surveys table alone, so a resource the patient
 * had engaged with still showed "Sent" here while the Resources tab
 * showed "Engaged". The gate is gone, so a resource status starts
 * showing the moment the API resolves one — until then `assetStatus`
 * stays null for resources and this returns exactly what it did before.
 *
 * Returns `null` for a step the journey has not reached.
 */
export const stepStatusPresentation = stepState => {
  if (!stepState || (!stepState.status && !stepState.assetStatus)) return null;
  const { status, assetStatus, assetInstanceUUID } = stepState;
  const note = STEP_STATUSES_WORTH_SHOWING.includes(status) ? status : null;
  if (assetStatus) {
    return { asset: assetStatus, sent: false, note };
  }
  return { asset: null, sent: Boolean(assetInstanceUUID), note };
};

/**
 * Where a step's "view report" click should go, or null when there is
 * nothing to open.
 *
 * Only survey assets have a report, and the report route needs the
 * survey's status in the URL — the backend sends it as `assetStatus`
 * for surveys and omits it when the survey has been deleted, which is
 * exactly the case where the link must not be built.
 */
export const getStepReportTarget = stepState => {
  if (!stepState || stepState.assetType !== 'survey') return null;
  if (!stepState.assetInstanceUUID || !stepState.assetStatus) return null;
  return { assessmentUuid: stepState.assetInstanceUUID, status: stepState.assetStatus };
};
